<script setup>
import { ref, onServerPrefetch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import RecommendItem from './RecommendItem.vue'
import { useGlobalHeader } from '../composable/useGlobalHeader.js'
import { useStore } from '../store.js'

const store = useStore()
const { headerMenu } = storeToRefs(store)
const props = defineProps({
  devDomains: { type: Object, default: () => {} }
})
const { getHeaderMenu } = useGlobalHeader(props.devDomains)

const isMenu = defineModel({
  prop: 'isMenu',
  type: Boolean
})

const emit = defineEmits(['overlayMenu'])
const openMenu = () => {
  emit('overlayMenu')
}

const selectedIndex = ref(0)

onServerPrefetch(async () => {
  headerMenu.value = await getHeaderMenu()
})

onMounted(async () => {
  if (!headerMenu.value) headerMenu.value = await getHeaderMenu()
})

</script>

<template>
  <section
    v-if="headerMenu"
    class="headerBottom"
    :class="{ 'data-menu': isMenu }"
  >
    <div class="menu">
      <ul class="menu-inner wrap">
        <template
          v-for="item, i in headerMenu"
          :key="i"
        >
          <li
            class="menu-item"
            :class="{ 'is-selected': selectedIndex === i }"
            @click="selectedIndex = i"
          >
            {{ item.title }}
          </li>
        </template>
      </ul>
    </div>
    <div
      class="item"
    >
      <ul
        v-for="(item, index) in headerMenu"
        v-show="selectedIndex === index"
        :key="index"
        class="wrap item-inner"
      >
        <li
          v-for="content in item?.contents"
          :key="content"
        >
          <RecommendItem
            :text="content.title"
            :link="content.url"
            :icon="content.icon_new"
          />
        </li>
      </ul>
      <button
        class="item-other"
        @click="openMenu"
      >
        <img
          src="https://gvs.weathernews.jp/onebox/img/header/icon_hamburger.svg"
          width="15"
          height="15"
          alt="他メニュー"
        >
      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.globalHeader {
  .headerBottom {
    overflow-y: hidden;
  }
  &.is-fixed {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 100;
    .menu {
      position: relative;
      transform: translateY(-100%);
      z-index: 9;
      opacity: 0;
      transition: .3s ease-in-out .3s;
    }
    .item {
      height: 0;
      position: relative;
      opacity: 0;
      transform: translateY(-100%);
      z-index: 8;
      box-shadow: 0 2px 4px 0 rgba(#000, .15);
      transition: height .1s ease-in-out .7s, transform .3s ease-in-out .3s, opacity .3s ease-in-out .3s;
      pointer-events: none;
    }
    &[data-visible='off'] {
      .headerTop {
        transform: translateY(-100%);
        opacity: 0;
        pointer-events: none;
      }
      .menu {
        pointer-events: none;
      }
      .item {
        pointer-events: none;
      }
    }
    .data-menu {
      .menu {
        pointer-events: auto;
        opacity: 1;
        transform: translateY(0);
      }
      .item {
        height: 42px;
        pointer-events: auto;
        opacity: 1;
        transform: translateY(0);
        transition: transform .3s ease-in-out .3s, opacity .3s ease-in-out .3s;
      }
    }
  }
  .wrap {
    width: 100%;
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
  .inner {
    display: flex;
    gap: 10px;
  }
  .menu {
    display: none;
    background-color: #0c419a;
    &-inner {
      display: flex;
    }
    &-item {
      position: relative;
      flex: 1;
      padding: 5px 24px;
      background: linear-gradient(180deg, rgba(#fff, .13) 0%, rgba(#fff, 0) 100%);
      color: #fff;
      font-size: 17px;
      font-weight: bold;
      line-height: 26px;
      text-align: center;
      cursor: pointer;
      white-space: nowrap;
      &::before {
        position: absolute;
        top: 50%;
        right: 9px;
        transform: translateY(-50%);
        width: 14px;
        height: 10px;
        background-color: #fff;
        clip-path: polygon(0 0, 100% 0, 50% 100%);
        content: '';
      }
      &.is-selected {
        background: linear-gradient(180deg, rgba(#000, .33) 0%, rgba(90, 90, 90, 0) 100%);
      }
    }
  }
  .item {
    display: flex;
    background-color: #f0f3f6;
    &-inner {
      display: flex;
      padding: 6px;
      flex: 1;
      overflow-x: scroll;
      gap: 3px;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    &-other {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 11px 13px;
      text-align: center;
      background: linear-gradient(180deg, rgba(#fff, .25) 0%, rgba(#fff, .08) 100%), #0c419a;
      &::before {
        position: absolute;
        top: 0;
        left: -10px;
        width: 10px;
        height: 100%;
        background: linear-gradient(270deg, rgba(#000, .25) 0%, rgba(#000, 0) 100%);
        content: '';
      }
    }
  }
  @media screen and (min-width: 830px) {
    &.is-fixed {
      .menu {
        transform: translateY(0);
        opacity: 1;
      }
      .item{
        height: 42px;
        transform: translateY(0);
        opacity: 1;
        pointer-events: auto;
      }
    }
    .menu {
      display: block;
      &-item {
        &:not(.is-selected) {
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
    }
    .item {
      &-inner {
        gap: 6px;
        padding-bottom: 4px;
        &::-webkit-scrollbar {
          display: block;
          width: 4px;
          height: 4px;
        }
        &::-webkit-scrollbar-track {
          border-radius: 100vmax;
          background-color: transparent;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 100vmax;
          background-color: #808080;
          cursor: pointer;
        }
      }
      &-other {
        display: none;
      }
    }
  }
}
</style>
